import React, { useEffect, useState, useRef } from "react";
import { notification } from "antd";
import { FileImageOutlined, TableOutlined, DatabaseOutlined } from "@ant-design/icons";
import { scaleBand, scaleLinear } from "d3-scale";
import { axisBottom, axisLeft, axisRight } from "d3-axis";
import { select } from "d3-selection";
import { ConditionalTooltip } from "../ConditionalTooltip";

export type BarChartData = {
    title: string;
    yAxisLabel?: string; // Primary (left) Y-axis label - kept for backward compatibility
    yAxisLabels: string[]; // Array of 1-2 distinct Y-axis labels (left, then right if dual)
    isStacked: boolean;
    categories: string[];
    series: Array<{
        name: string;
        color: string;
        yAxisLabel: string; // Which Y-axis this series belongs to
    }>;
    segments: Array<{
        category: string;
        series: string;
        value: number;
    }>;
    // For backward compatibility with regular bars
    bars?: Array<{
        label: string;
        value: number;
        color: string;
        yAxisLabel: string; // Which Y-axis this bar belongs to
    }>;
};

export const isBarChartData = (columns: any[], dataSource: any[]): boolean => {
    if (!columns || !dataSource || dataSource.length === 0) {
        return false;
    }

    const columnTitles = columns.map(col => (col.title || "").trim());

    // Check for regular bar chart (5 columns)
    if (columns.length === 5) {
        const requiredColumns = ["bar_category", "bar_value", "bar_color", "bar_title", "bar_y_axis_label"];
        return requiredColumns.every(reqCol => columnTitles.includes(reqCol));
    }

    // Check for stacked bar chart (6 columns)
    if (columns.length === 6) {
        const requiredColumns = ["bar_category", "bar_value", "bar_series", "bar_color", "bar_title", "bar_y_axis_label"];
        return requiredColumns.every(reqCol => columnTitles.includes(reqCol));
    }

    return false;
};

export const extractBarChartData = (columns: any[], dataSource: any[]): BarChartData | null => {
    if (!isBarChartData(columns, dataSource)) {
        return null;
    }

    const categoryCol = columns.find(col => (col.title || "").trim() === "bar_category");
    const valueCol = columns.find(col => (col.title || "").trim() === "bar_value");
    const colorCol = columns.find(col => (col.title || "").trim() === "bar_color");
    const titleCol = columns.find(col => (col.title || "").trim() === "bar_title");
    const yAxisLabelCol = columns.find(col => (col.title || "").trim() === "bar_y_axis_label");
    const seriesCol = columns.find(col => (col.title || "").trim() === "bar_series");

    if (!categoryCol || !valueCol || !colorCol || !titleCol || !yAxisLabelCol) {
        return null;
    }

    const title = String(dataSource[0]?.[titleCol.dataIndex] || "").trim();

    // Collect all distinct Y-axis labels (preserve order of first appearance)
    const yAxisLabels: string[] = [];
    dataSource.forEach(row => {
        const yLabel = String(row[yAxisLabelCol.dataIndex] || "").trim();
        if (!yAxisLabels.includes(yLabel)) {
            yAxisLabels.push(yLabel);
        }
    });
    const finalYAxisLabels = yAxisLabels.slice(0, 2);

    // Check if this is a stacked bar chart (has bar_series column)
    if (seriesCol) {
        // Stacked bar chart
        const segments: Array<{ category: string; series: string; value: number }> = [];
        const seriesMap = new Map<string, { name: string; color: string; yAxisLabel: string }>();
        const categorySet = new Set<string>();
        const seriesYAxisLabelMap = new Map<string, string>(); // series name -> y-axis label

        dataSource.forEach(row => {
            const valueStr = String(row[valueCol.dataIndex] || "0").replace(/,/g, "");
            const category = String(row[categoryCol.dataIndex] || "").trim();
            const seriesName = String(row[seriesCol.dataIndex] || "").trim();
            const color = String(row[colorCol.dataIndex] || "#000000").trim();
            const yLabel = String(row[yAxisLabelCol.dataIndex] || "").trim();

            categorySet.add(category);

            // Track Y-axis label for first row of each series
            if (!seriesYAxisLabelMap.has(seriesName)) {
                seriesYAxisLabelMap.set(seriesName, yLabel);
            }

            if (!seriesMap.has(seriesName)) {
                seriesMap.set(seriesName, {
                    name: seriesName,
                    color: color, // Use first segment's color for the series
                    yAxisLabel: yLabel
                });
            }

            segments.push({
                category,
                series: seriesName,
                value: parseFloat(valueStr) || 0
            });
        });

        const categories = Array.from(categorySet);
        const series = Array.from(seriesMap.values());

        return {
            title,
            yAxisLabel: finalYAxisLabels[0] || undefined,
            yAxisLabels: finalYAxisLabels,
            isStacked: true,
            categories,
            series,
            segments
        };
    } else {
        // Regular bar chart (backward compatibility)
        const bars = dataSource.map(row => {
            const valueStr = String(row[valueCol.dataIndex] || "0").replace(/,/g, "");
            const yLabel = String(row[yAxisLabelCol.dataIndex] || "").trim();
            return {
                label: String(row[categoryCol.dataIndex] || "").trim(),
                value: parseFloat(valueStr) || 0,
                color: String(row[colorCol.dataIndex] || "#000000").trim(),
                yAxisLabel: yLabel
            };
        });

        return {
            title,
            yAxisLabel: finalYAxisLabels[0] || undefined,
            yAxisLabels: finalYAxisLabels,
            isStacked: false,
            categories: [],
            series: [],
            segments: [],
            bars
        };
    }
};

export function BarChart(props: {
    data: BarChartData;
    darkMode: boolean;
    jobAlias?: string;
    onDownloadCsv?: () => void;
    onDownloadPng?: () => void;
    onAddQuery?: () => void;
}) {
    const { data, darkMode, onDownloadCsv, onDownloadPng, onAddQuery } = props;
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
    const [pressedCsv, setPressedCsv] = useState(false);
    const [pressedPng, setPressedPng] = useState(false);
    const [isAddQueryHovered, setIsAddQueryHovered] = useState(false);
    const [pressedAddQuery, setPressedAddQuery] = useState(false);

    useEffect(() => {
        if (!containerRef.current) return;

        let currentWidth = 0;
        let currentHeight = 0;
        let isInitialized = false;

        const updateDimensions = (width: number) => {
            if (width > 0) {
                const chartWidth = Math.min(width - 40, 1200);
                const chartHeight = Math.min(chartWidth * 0.7, 800);

                // Only update if dimensions actually changed
                if (chartWidth !== currentWidth || chartHeight !== currentHeight) {
                    currentWidth = chartWidth;
                    currentHeight = chartHeight;
                    setDimensions({ width: chartWidth, height: chartHeight });
                }
            }
        };

        // Initial dimensions - wait for next frame to ensure layout is complete
        requestAnimationFrame(() => {
            if (!containerRef.current) return;
            const initialWidth = containerRef.current.clientWidth || containerRef.current.offsetWidth;
            if (initialWidth > 0) {
                updateDimensions(initialWidth);
                isInitialized = true;
            }
        });

        // Observe container for size changes with a small debounce
        let resizeTimeout: NodeJS.Timeout;
        const resizeObserver = new ResizeObserver(entries => {
            if (!isInitialized) return; // Ignore resize events until initial dimensions are set
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                for (const entry of entries) {
                    const { width } = entry.contentRect;
                    if (width > 0) {
                        updateDimensions(width);
                    }
                }
            }, 10);
        });

        // Delay observer setup slightly to avoid immediate fire
        const setupTimeout = setTimeout(() => {
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
        }, 50);

        return () => {
            clearTimeout(resizeTimeout);
            clearTimeout(setupTimeout);
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!svgRef.current) return;
        if (!data.isStacked && (!data.bars || data.bars.length === 0)) return;
        if (data.isStacked && data.segments.length === 0) return;

        const svg = select(svgRef.current);
        svg.selectAll("*").remove();

        // Check for dual Y-axis mode
        const isDualYAxis = data.yAxisLabels.length === 2;
        const leftYAxisLabel = data.yAxisLabels[0] || "";
        const rightYAxisLabel = data.yAxisLabels[1] || "";

        // Increase right margin for dual Y-axis or stacked charts
        const baseRightMargin = data.isStacked ? 200 : 40;
        const margin = { top: 40, right: isDualYAxis ? Math.max(baseRightMargin, 350) : baseRightMargin, bottom: 80, left: 80 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        if (data.isStacked) {
            // Stacked bar chart rendering with dual Y-axis support
            // For stacked charts, separate categories by which axis their series use
            const leftAxisValues: number[] = [];
            const rightAxisValues: number[] = [];

            // Calculate totals per category, separated by axis
            const categoryTotalsLeft = new Map<string, number>();
            const categoryTotalsRight = new Map<string, number>();

            data.segments.forEach(segment => {
                const seriesData = data.series.find(s => s.name === segment.series);
                const isRightAxis = isDualYAxis && seriesData?.yAxisLabel === rightYAxisLabel;

                if (isRightAxis) {
                    rightAxisValues.push(segment.value);
                    const current = categoryTotalsRight.get(segment.category) || 0;
                    categoryTotalsRight.set(segment.category, current + segment.value);
                } else {
                    leftAxisValues.push(segment.value);
                    const current = categoryTotalsLeft.get(segment.category) || 0;
                    categoryTotalsLeft.set(segment.category, current + segment.value);
                }
            });

            // For stacked charts, use the max of category totals
            const maxValueLeft = Math.max(...Array.from(categoryTotalsLeft.values()), 0);
            const maxValueRight = Math.max(...Array.from(categoryTotalsRight.values()), 0);

            const yScaleLeft = scaleLinear()
                .domain([0, maxValueLeft * 1.1 || 1])
                .range([height, 0]);

            const yScaleRight = scaleLinear()
                .domain([0, maxValueRight * 1.1 || 1])
                .range([height, 0]);

            const xScale = scaleBand()
                .domain(data.categories)
                .range([0, width])
                .padding(0.2);

            // Helper to get correct Y scale for a series
            const getYScale = (seriesYAxisLabel: string) => {
                if (isDualYAxis && seriesYAxisLabel === rightYAxisLabel) {
                    return yScaleRight;
                }
                return yScaleLeft;
            };

            // Set background to transparent
            svg.append("rect")
                .attr("width", dimensions.width)
                .attr("height", dimensions.height)
                .attr("fill", "transparent");

            const g = svg.append("g")
                .attr("transform", `translate(${margin.left},${margin.top})`);

            // Left Y-axis
            g.append("g")
                .call(axisLeft(yScaleLeft))
                .selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000");

            // Right Y-axis (only if dual Y-axis mode)
            if (isDualYAxis) {
                const rightAxisGroup = g.append("g")
                    .attr("transform", `translate(${width}, 0)`)
                    .call(axisRight(yScaleRight));
                rightAxisGroup.selectAll("text")
                    .attr("fill", darkMode ? "#ffffff" : "#000000");
                rightAxisGroup.selectAll(".domain, .tick line")
                    .attr("stroke", darkMode ? "#666666" : "#cccccc");
            }

            // Axis lines
            g.selectAll(".domain, .tick line")
                .attr("stroke", darkMode ? "#666666" : "#cccccc");

            // Left Y-axis label
            if (leftYAxisLabel) {
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", -margin.left + 20)
                    .attr("x", -height / 2)
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "14px")
                    .text(leftYAxisLabel);
            }

            // Right Y-axis label
            if (isDualYAxis && rightYAxisLabel) {
                g.append("text")
                    .attr("transform", "rotate(90)")
                    .attr("y", -width - 60)
                    .attr("x", height / 2)
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "14px")
                    .text(rightYAxisLabel);
            }

            // Group segments by category and axis, calculate stacking positions
            const categoryGroupsLeft = new Map<string, Array<{ series: string; value: number; y0: number; y1: number }>>();
            const categoryGroupsRight = new Map<string, Array<{ series: string; value: number; y0: number; y1: number }>>();

            data.categories.forEach(category => {
                const categorySegments = data.segments.filter(s => s.category === category);
                const seriesOrder = data.series.map(s => s.name);

                // Sort segments by series order
                categorySegments.sort((a, b) => {
                    const aIndex = seriesOrder.indexOf(a.series);
                    const bIndex = seriesOrder.indexOf(b.series);
                    return aIndex - bIndex;
                });

                let y0Left = 0;
                let y0Right = 0;
                const stackedLeft: Array<{ series: string; value: number; y0: number; y1: number }> = [];
                const stackedRight: Array<{ series: string; value: number; y0: number; y1: number }> = [];

                categorySegments.forEach(segment => {
                    const seriesData = data.series.find(s => s.name === segment.series);
                    const isRightAxis = isDualYAxis && seriesData?.yAxisLabel === rightYAxisLabel;

                    if (isRightAxis) {
                        const y1 = y0Right + segment.value;
                        stackedRight.push({
                            series: segment.series,
                            value: segment.value,
                            y0: y0Right,
                            y1
                        });
                        y0Right = y1;
                    } else {
                        const y1 = y0Left + segment.value;
                        stackedLeft.push({
                            series: segment.series,
                            value: segment.value,
                            y0: y0Left,
                            y1
                        });
                        y0Left = y1;
                    }
                });

                categoryGroupsLeft.set(category, stackedLeft);
                categoryGroupsRight.set(category, stackedRight);
            });

            // Render stacked segments for each axis
            data.categories.forEach(category => {
                const x = xScale(category) || 0;
                const barWidth = isDualYAxis ? xScale.bandwidth() / 2 - 2 : xScale.bandwidth();

                // Left axis bars
                const stackedLeft = categoryGroupsLeft.get(category) || [];
                stackedLeft.forEach(segment => {
                    const seriesData = data.series.find(s => s.name === segment.series);
                    if (!seriesData) return;

                    const segmentHeight = yScaleLeft(segment.y0) - yScaleLeft(segment.y1);
                    const segmentY = yScaleLeft(segment.y1);

                    g.append("rect")
                        .attr("class", "bar-segment")
                        .attr("x", x)
                        .attr("y", segmentY)
                        .attr("width", barWidth)
                        .attr("height", segmentHeight)
                        .attr("fill", seriesData.color)
                        .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                        .attr("stroke-width", 1)
                        .on("mouseover", function(this: SVGRectElement) {
                            select(this)
                                .attr("opacity", 0.8)
                                .attr("stroke-width", 2);

                            const tooltip = g.append("g")
                                .attr("class", "tooltip")
                                .attr("transform", `translate(${x + barWidth / 2},${segmentY - 10})`);

                            const tooltipText = `${seriesData.name}: ${segment.value.toLocaleString()}`;
                            const textElement = tooltip.append("text")
                                .attr("text-anchor", "middle")
                                .attr("fill", darkMode ? "#ffffff" : "#000000")
                                .attr("font-size", "11px")
                                .text(tooltipText);

                            const textBBox = textElement.node()?.getBBox();
                            if (textBBox) {
                                tooltip.insert("rect", "text")
                                    .attr("x", textBBox.x - 5)
                                    .attr("y", textBBox.y - 2)
                                    .attr("width", textBBox.width + 10)
                                    .attr("height", textBBox.height + 4)
                                    .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                    .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                    .attr("rx", 4);
                            }
                        })
                        .on("mouseout", function(this: SVGRectElement) {
                            select(this)
                                .attr("opacity", 1)
                                .attr("stroke-width", 1);
                            g.selectAll(".tooltip").remove();
                        });
                });

                // Right axis bars (only if dual Y-axis)
                if (isDualYAxis) {
                    const stackedRight = categoryGroupsRight.get(category) || [];
                    stackedRight.forEach(segment => {
                        const seriesData = data.series.find(s => s.name === segment.series);
                        if (!seriesData) return;

                        const segmentHeight = yScaleRight(segment.y0) - yScaleRight(segment.y1);
                        const segmentY = yScaleRight(segment.y1);

                        g.append("rect")
                            .attr("class", "bar-segment")
                            .attr("x", x + barWidth + 4)
                            .attr("y", segmentY)
                            .attr("width", barWidth)
                            .attr("height", segmentHeight)
                            .attr("fill", seriesData.color)
                            .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                            .attr("stroke-width", 1)
                            .on("mouseover", function(this: SVGRectElement) {
                                select(this)
                                    .attr("opacity", 0.8)
                                    .attr("stroke-width", 2);

                                const tooltip = g.append("g")
                                    .attr("class", "tooltip")
                                    .attr("transform", `translate(${x + barWidth * 1.5 + 4},${segmentY - 10})`);

                                const tooltipText = `${seriesData.name}: ${segment.value.toLocaleString()}`;
                                const textElement = tooltip.append("text")
                                    .attr("text-anchor", "middle")
                                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                                    .attr("font-size", "11px")
                                    .text(tooltipText);

                                const textBBox = textElement.node()?.getBBox();
                                if (textBBox) {
                                    tooltip.insert("rect", "text")
                                        .attr("x", textBBox.x - 5)
                                        .attr("y", textBBox.y - 2)
                                        .attr("width", textBBox.width + 10)
                                        .attr("height", textBBox.height + 4)
                                        .attr("fill", darkMode ? "#1f1f1f" : "#ffffff")
                                        .attr("stroke", darkMode ? "#666666" : "#cccccc")
                                        .attr("rx", 4);
                                }
                            })
                            .on("mouseout", function(this: SVGRectElement) {
                                select(this)
                                    .attr("opacity", 1)
                                    .attr("stroke-width", 1);
                                g.selectAll(".tooltip").remove();
                            });
                    });
                }
            });

            // Category labels at bottom (rotated 270 degrees)
            g.selectAll(".category-label")
                .data(data.categories)
                .enter()
                .append("text")
                .attr("class", "category-label")
                .attr("text-anchor", "start")
                .attr("alignment-baseline", "baseline")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "12px")
                .attr("font-weight", "500")
                .attr("transform", (category) => {
                    const x = (xScale(category) || 0) + xScale.bandwidth() / 2;
                    const y = height - 5;
                    return `translate(${x},${y}) rotate(270)`;
                })
                .text((category) => category);

            // Total value labels below bars
            const categoryTotals = new Map<string, number>();
            data.segments.forEach(segment => {
                const current = categoryTotals.get(segment.category) || 0;
                categoryTotals.set(segment.category, current + segment.value);
            });

            g.selectAll(".value-label")
                .data(data.categories)
                .enter()
                .append("text")
                .attr("class", "value-label")
                .attr("x", (category) => (xScale(category) || 0) + xScale.bandwidth() / 2)
                .attr("y", height + 25)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "12px")
                .attr("font-weight", "500")
                .text((category) => {
                    const total = categoryTotals.get(category) || 0;
                    return total.toLocaleString();
                });

            // Legend - position further right when dual Y-axis to avoid overlapping the right axis
            const legendXOffset = isDualYAxis ? 90 : 20;
            const legend = svg.append("g")
                .attr("class", "legend")
                .attr("transform", `translate(${dimensions.width - margin.right + legendXOffset}, ${margin.top + 20})`);

            if (isDualYAxis) {
                // Group series by axis
                const leftAxisSeries = data.series.filter(s => s.yAxisLabel !== rightYAxisLabel);
                const rightAxisSeries = data.series.filter(s => s.yAxisLabel === rightYAxisLabel);
                let yOffset = 0;

                // Left axis label
                if (leftAxisSeries.length > 0) {
                    legend.append("text")
                        .attr("x", 0)
                        .attr("y", yOffset)
                        .attr("fill", darkMode ? "#888888" : "#999999")
                        .attr("font-size", "10px")
                        .attr("font-weight", "500")
                        .text("← Left Axis");
                    yOffset += 18;

                    leftAxisSeries.forEach((seriesData) => {
                        const item = legend.append("g")
                            .attr("transform", `translate(0, ${yOffset})`);

                        item.append("rect")
                            .attr("width", 15)
                            .attr("height", 15)
                            .attr("fill", seriesData.color);

                        item.append("text")
                            .attr("x", 20)
                            .attr("y", 12)
                            .attr("fill", darkMode ? "#ffffff" : "#000000")
                            .attr("font-size", "12px")
                            .text(seriesData.name);

                        yOffset += 22;
                    });
                }

                // Right axis label
                if (rightAxisSeries.length > 0) {
                    yOffset += 16; // Extra spacing between groups
                    legend.append("text")
                        .attr("x", 0)
                        .attr("y", yOffset)
                        .attr("fill", darkMode ? "#888888" : "#999999")
                        .attr("font-size", "10px")
                        .attr("font-weight", "500")
                        .text("Right Axis →");
                    yOffset += 18;

                    rightAxisSeries.forEach((seriesData) => {
                        const item = legend.append("g")
                            .attr("transform", `translate(0, ${yOffset})`);

                        item.append("rect")
                            .attr("width", 15)
                            .attr("height", 15)
                            .attr("fill", seriesData.color);

                        item.append("text")
                            .attr("x", 20)
                            .attr("y", 12)
                            .attr("fill", darkMode ? "#ffffff" : "#000000")
                            .attr("font-size", "12px")
                            .text(seriesData.name);

                        yOffset += 22;
                    });
                }
            } else {
                // Single Y-axis: simple legend
                const legendItems = legend.selectAll(".legend-item")
                    .data(data.series)
                    .enter()
                    .append("g")
                    .attr("class", "legend-item")
                    .attr("transform", (_d, i: number) => `translate(0, ${i * 25})`);

                legendItems.append("rect")
                    .attr("width", 15)
                    .attr("height", 15)
                    .attr("fill", d => d.color);

                legendItems.append("text")
                    .attr("x", 20)
                    .attr("y", 12)
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "12px")
                    .text(d => d.name);
            }

        } else {
            // Regular bar chart rendering with dual Y-axis support
            if (!data.bars) return;

            // Separate bars by axis
            const leftAxisBars = data.bars.filter(bar => !isDualYAxis || bar.yAxisLabel !== rightYAxisLabel);
            const rightAxisBars = data.bars.filter(bar => isDualYAxis && bar.yAxisLabel === rightYAxisLabel);

            const maxValueLeft = leftAxisBars.length > 0 ? Math.max(...leftAxisBars.map(bar => bar.value), 0) : 0;
            const maxValueRight = rightAxisBars.length > 0 ? Math.max(...rightAxisBars.map(bar => bar.value), 0) : 0;

            const yScaleLeft = scaleLinear()
                .domain([0, maxValueLeft * 1.1 || 1])
                .range([height, 0]);

            const yScaleRight = scaleLinear()
                .domain([0, maxValueRight * 1.1 || 1])
                .range([height, 0]);

            const xScale = scaleBand()
                .domain(data.bars.map(bar => bar.label))
                .range([0, width])
                .padding(0.2);

            // Set background to transparent
            svg.append("rect")
                .attr("width", dimensions.width)
                .attr("height", dimensions.height)
                .attr("fill", "transparent");

            const g = svg.append("g")
                .attr("transform", `translate(${margin.left},${margin.top})`);

            // Left Y-axis
            g.append("g")
                .call(axisLeft(yScaleLeft))
                .selectAll("text")
                .attr("fill", darkMode ? "#ffffff" : "#000000");

            // Right Y-axis (only if dual Y-axis mode)
            if (isDualYAxis) {
                const rightAxisGroup = g.append("g")
                    .attr("transform", `translate(${width}, 0)`)
                    .call(axisRight(yScaleRight));
                rightAxisGroup.selectAll("text")
                    .attr("fill", darkMode ? "#ffffff" : "#000000");
                rightAxisGroup.selectAll(".domain, .tick line")
                    .attr("stroke", darkMode ? "#666666" : "#cccccc");
            }

            // Axis lines
            g.selectAll(".domain, .tick line")
                .attr("stroke", darkMode ? "#666666" : "#cccccc");

            // Left Y-axis label
            if (leftYAxisLabel) {
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", -margin.left + 20)
                    .attr("x", -height / 2)
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "14px")
                    .text(leftYAxisLabel);
            }

            // Right Y-axis label
            if (isDualYAxis && rightYAxisLabel) {
                g.append("text")
                    .attr("transform", "rotate(90)")
                    .attr("y", -width - 60)
                    .attr("x", height / 2)
                    .attr("text-anchor", "middle")
                    .attr("fill", darkMode ? "#ffffff" : "#000000")
                    .attr("font-size", "14px")
                    .text(rightYAxisLabel);
            }

            // Bars - use appropriate scale based on axis
            g.selectAll(".bar")
                .data(data.bars)
                .enter()
                .append("rect")
                .attr("class", "bar")
                .attr("x", (d) => xScale(d.label) || 0)
                .attr("y", (d) => {
                    const isRightAxis = isDualYAxis && d.yAxisLabel === rightYAxisLabel;
                    const yScale = isRightAxis ? yScaleRight : yScaleLeft;
                    return yScale(d.value);
                })
                .attr("width", xScale.bandwidth())
                .attr("height", (d) => {
                    const isRightAxis = isDualYAxis && d.yAxisLabel === rightYAxisLabel;
                    const yScale = isRightAxis ? yScaleRight : yScaleLeft;
                    return height - yScale(d.value);
                })
                .attr("fill", (d) => d.color)
                .attr("stroke", darkMode ? "#2d2d2d" : "#ffffff")
                .attr("stroke-width", 1)
                .on("mouseover", function(this: SVGRectElement) {
                    select(this)
                        .attr("opacity", 0.8)
                        .attr("stroke-width", 2);
                })
                .on("mouseout", function(this: SVGRectElement) {
                    select(this)
                        .attr("opacity", 1)
                        .attr("stroke-width", 1);
                });

            // Category labels inside bars (starting at bottom, extending upward) - rotated 270 degrees
            g.selectAll(".category-label")
                .data(data.bars)
                .enter()
                .append("text")
                .attr("class", "category-label")
                .attr("x", 0)
                .attr("y", 0)
                .attr("text-anchor", "start")
                .attr("alignment-baseline", "baseline")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "12px")
                .attr("font-weight", "500")
                .attr("transform", (d) => {
                    const x = (xScale(d.label) || 0) + xScale.bandwidth() / 2;
                    const y = height - 5;
                    return `translate(${x},${y}) rotate(270)`;
                })
                .text((d) => d.label);

            // Value labels below bars (not rotated)
            g.selectAll(".value-label")
                .data(data.bars)
                .enter()
                .append("text")
                .attr("class", "value-label")
                .attr("x", (d) => (xScale(d.label) || 0) + xScale.bandwidth() / 2)
                .attr("y", height + 25)
                .attr("text-anchor", "middle")
                .attr("fill", darkMode ? "#ffffff" : "#000000")
                .attr("font-size", "12px")
                .attr("font-weight", "500")
                .text((d) => d.value.toLocaleString());
        }

    }, [data, dimensions, darkMode]);

    const handleDownloadCsv = () => {
        setPressedCsv(true);
        setTimeout(() => setPressedCsv(false), 200);
        if (onDownloadCsv) {
            onDownloadCsv();
        }
    };

    const handleDownloadPng = async () => {
        if (!svgRef.current) return;

        setPressedPng(true);
        setTimeout(() => setPressedPng(false), 200);

        if (onDownloadPng) {
            onDownloadPng();
        }

        try {
            const svg = svgRef.current.cloneNode(true) as SVGElement;

            // Set SVG dimensions
            svg.setAttribute('width', dimensions.width.toString());
            svg.setAttribute('height', dimensions.height.toString());
            svg.setAttribute('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`);

            const svgData = new XMLSerializer().serializeToString(svg);

            // Create a data URL with proper namespace
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            // Scale factor for higher resolution (2x for retina quality)
            const scale = 2;

            img.onload = () => {
                // Set canvas size with scale factor for higher resolution
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;

                if (ctx) {
                    // Scale the context for high DPI
                    ctx.scale(scale, scale);

                    // Fill background (transparent)
                    ctx.fillStyle = 'transparent';
                    ctx.fillRect(0, 0, img.width, img.height);

                    // Draw the SVG image
                    ctx.drawImage(img, 0, 0);

                    // Convert to blob and download
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const blobUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.setAttribute('href', blobUrl);
                            const filename = `${data.title.replace(/[<>:"/\\|?*]/g, '')}.png`;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(blobUrl);
                        }
                    }, 'image/png', 1.0); // Maximum quality
                }
                URL.revokeObjectURL(url);
            };

            img.onerror = () => {
                console.error('Error loading SVG image');
                URL.revokeObjectURL(url);
                notification.error({
                    message: 'Failed to download PNG',
                    description: 'An error occurred while generating the PNG image.',
                    placement: 'bottomLeft',
                    duration: 3
                });
            };

            img.src = url;
        } catch (error) {
            console.error('Error downloading PNG:', error);
            notification.error({
                message: 'Failed to download PNG',
                description: 'An error occurred while generating the PNG image.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    return (
        <div ref={containerRef} style={{ marginBottom: "1em", display: "flex", flexDirection: "column", alignItems: "center", position: "relative", width: "100%" }}>
            <h3 style={{
                color: darkMode ? "#ffffff" : "#000000",
                marginBottom: "1em",
                textAlign: "center"
            }}>
                {data.title}
            </h3>
            <div style={{ position: "relative", display: "inline-block" }}>
                <svg
                    ref={svgRef}
                    width={dimensions.width}
                    height={dimensions.height}
                    style={{ maxWidth: "100%", overflow: "visible" }}
                    viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                />
                <div style={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    display: "flex",
                    gap: "0.75em"
                }}>
                    <ConditionalTooltip title="Download bar chart as PNG">
                        <button
                            onClick={handleDownloadPng}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedPng ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <FileImageOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Download bar chart data as CSV">
                        <button
                            onClick={handleDownloadCsv}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: darkMode ? "#888888" : "#AFAFAF",
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedCsv ? "scale(0.85)" : "scale(1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = darkMode ? "#ffffff" : "#000000";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = darkMode ? "#888888" : "#AFAFAF";
                            }}
                        >
                            <TableOutlined />
                        </button>
                    </ConditionalTooltip>
                    <ConditionalTooltip title="Add query">
                        <button
                            onClick={() => {
                                setPressedAddQuery(true);
                                setTimeout(() => setPressedAddQuery(false), 200);
                                onAddQuery?.();
                            }}
                            onMouseEnter={() => setIsAddQueryHovered(true)}
                            onMouseLeave={() => setIsAddQueryHovered(false)}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "20px",
                                color: isAddQueryHovered
                                    ? (darkMode ? "#ffffff" : "#000000")
                                    : (darkMode ? "#888888" : "#AFAFAF"),
                                display: "flex",
                                alignItems: "center",
                                padding: "0",
                                transition: "color 0.2s, transform 0.1s",
                                transform: pressedAddQuery ? "scale(0.85)" : "scale(1)"
                            }}
                        >
                            <DatabaseOutlined />
                        </button>
                    </ConditionalTooltip>
                </div>
            </div>
        </div>
    );
};

